/**
 * MainPage Component (Chat-First Home)
 *
 * TWO phases, ONE continuous page — no navigation:
 *   1. "pick"     – Greeting + agent gallery. Nothing else: this page's only
 *                   job is choosing who to talk to.
 *   2. "chatting" – Welcome elements animate out, ConversationPanel mounts
 *                   for the chosen agent and the user types there.
 *
 * PICKING IS STARTING. Clicking any card — agent or Concierge — opens the
 * conversation immediately. There used to be a middle "ready" phase where an
 * agent was selected and a composer on THIS page was enabled, so the first
 * message was typed here and handed to the panel via autoSend. That composer
 * is gone (it duplicated the chat's own input one screen later), and with it
 * the middle phase, the carousel-index selection model and the model picker.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTenantPath } from '@/routing';
import { BotAvatar } from '@/components/common/BotAvatar';
import { resolveAgentTextureColor } from '@/brand/textures';
import { Sidebar } from '@/components/layout/Sidebar';
import { ConversationPanel } from '@modules/conversations/components/ConversationPanel';
import { useAuth } from '@modules/auth';
import { botService } from '@modules/chatbots';
import { useLanguage } from '@/hooks/useLanguage';
import { useFeature } from '@/hooks/useFeature';
import {
  buildConciergeCarouselBot,
  isConciergeCarouselBot,
  CONCIERGE_CARD_CLASS,
} from '@modules/concierge/components/conciergeCarouselItem';
import { AgentGallery } from '../components/agentGallery';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import { useKeyboardAwareViewport } from '@/hooks/useKeyboardAwareViewport';
import { capitalizeName } from '@/utils/getInitials';
import { useBrand, useBrandAssets, SOLID_AGENT_COLORS } from '@/brand';
import './MainPage.css';

type Phase = 'pick' | 'chatting';
// First-visit splash: M² logo overlays the page, then fades out as the
// home content staggers in. Only plays once per browser session.
type EntryPhase = 'splash' | 'revealing' | 'done';
const SPLASH_SESSION_KEY = 'corpbrain_splash_shown';

export default function MainPage() {
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useLanguage();
  // Concierge front door — the synthetic carousel card below. Gated by the
  // modules.concierge feature flag; available to every role.
  const conciergeEnabled = useFeature('modules.concierge');

  // Mobile keyboard handling (same hook used by ChatPage). Active during
  // the 'chatting' phase when ConversationPanel is mounted inside this
  // page — hook idempotently adds the body class and removes it on
  // unmount, so it's safe to mount on every phase.
  useKeyboardAwareViewport();

  // Brand-driven splash logo — falls back through the resolved brand chain
  // (tenant overrides → preset → platform default).
  const { icon: splashIcon } = useBrandAssets();
  /* Brand-driven visual treatments. Both default OFF in the base brand;
     M² preset turns both on. The semicircle's optional `color` overrides
     the agent-color follow behavior with a fixed hex. */
  const brand = useBrand();
  const semicircleEnabled = !!brand.homeSemicircle?.enabled;
  const semicircleColorOverride = brand.homeSemicircle?.color || null;

  // When the sidebar logo is clicked from another route, it navigates with
  // `state: { reset: true }` so we ignore any URL params that would otherwise
  // re-hydrate a selected agent or conversation. Without this gate, the logo
  // would silently re-open the chat the user just tried to escape.
  const skipParams = (location.state as { reset?: boolean } | null)?.reset === true;
  const conversationParam = skipParams ? null : searchParams.get('conversation');
  const botIdParam = skipParams ? null : searchParams.get('bot');

  // One-shot: consume the reset flag from history on mount so it doesn't
  // outlive this navigation (e.g. if the user later manually edits the URL).
  // Reads location.state inside the effect to keep deps honest — re-running
  // is harmless since replaceState is idempotent here.
  useEffect(() => {
    const state = (location.state as { reset?: boolean } | null);
    if (state?.reset) {
      window.history.replaceState({}, '', window.location.href);
    }
  }, [location.state]);

  const [phase, setPhase] = useState<Phase>('pick');
  const [entryPhase, setEntryPhase] = useState<EntryPhase>(() => {
    // Skip splash on back-nav within the same session or when deep-linked
    // into an existing conversation.
    if (typeof window === 'undefined') return 'done';
    if (sessionStorage.getItem(SPLASH_SESSION_KEY) === '1') return 'done';
    if (conversationParam) return 'done';
    return 'splash';
  });
  // Mirrors `phase` for the bot-loading effect below, which deliberately does
  // NOT list `phase` as a dependency — doing so would refire the bot fetches
  // on every phase change.
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const [selectedBotId, setSelectedBotId] = useState<string | null>(botIdParam);
  const [bots, setBots] = useState<any[]>([]);
  const [botsLoading, setBotsLoading] = useState(true);

  // Favorites / recents kept as ID SETS for the gallery's category tabs. The
  // bot-loading effect already fetches both for its re-ordering pass; it used
  // to merge them into one list and throw the origin away, so the tabs would
  // have had nothing to filter on.
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [recentIds, setRecentIds] = useState<Set<string>>(new Set());

  // Remount key for ConversationPanel. Bumped whenever a chat is opened or
  // reset, so returning to the picker and choosing a different agent gets a
  // clean panel instead of one hydrated from the previous conversation.
  const [chatKey, setChatKey] = useState(0);

  // Hovered agent's texture colour. The hero semicircle follows it, fading
  // between hues via the `transition: background` already on `.welcome-semi`.
  // This is where the agent's colour is FELT — the cards themselves stay flat
  // white so the grid reads as one calm surface.
  const [hoveredAgentColor, setHoveredAgentColor] = useState<string | null>(null);

  // Splash → revealing → done. Timings chosen to feel brisk (~1.85s total)
  // without stealing attention from the home content that follows.
  useEffect(() => {
    if (entryPhase === 'done') return;
    sessionStorage.setItem(SPLASH_SESSION_KEY, '1');
    const toReveal = setTimeout(() => setEntryPhase('revealing'), 950);
    const toDone = setTimeout(() => setEntryPhase('done'), 1850);
    return () => {
      clearTimeout(toReveal);
      clearTimeout(toDone);
    };
  }, []);

  // Reset to the agent-picker whenever a "new chat" is requested from
  // elsewhere (chat header button, mobile sidebar FAB). React Router can't
  // remount MainPage when we're already on '/' with chatting phase cached
  // in state, so a custom event is the cleanest way to reset.
  useEffect(() => {
    const resetToNewChat = () => {
      setPhase('pick');
      setSelectedBotId(null);
      setChatKey(k => k + 1);
    };
    window.addEventListener('corpbrain:newChat', resetToNewChat);
    return () => window.removeEventListener('corpbrain:newChat', resetToNewChat);
  }, []);

  // Load bots — THREE-PHASE render for instant perceived speed:
  //
  //   Phase 0 (cache, synchronous): hydrate the carousel from
  //     `mainpage_bots:<userId>` in sessionStorage so the first paint
  //     already shows the user's agents on repeat visits within a
  //     session. Cache is keyed per user so two users on the same
  //     browser don't see each other's lists.
  //
  //   Phase A (network, immediate replace): `botService.getCarouselBots()`
  //     hits the lightweight `/chatbots/carousel` endpoint — minimal
  //     payload, no permissions/shares/bundles, and Redis-cached on the
  //     server per (tenant, user). So the FIRST user of the day pays the
  //     cold-pool cost once and every later visitor (any user in the
  //     tenant) is served from Redis. Without this split, the welcome
  //     page waited on the full `/chatbots` payload — heavy enough that
  //     the cold-pool wake-up on Cloud Run made the first paint take
  //     ~60s on brij-staging.
  //
  //   Phase B (network, background re-order): favorites + recents
  //     resolve later and trigger a quiet re-shuffle so the
  //     "important" agents bubble to the front.
  useEffect(() => {
    let cancelled = false;
    const userId = user?.user_id || user?.email || 'anon';
    const cacheKey = `mainpage_bots:${userId}`;

    const applyOrder = (ordered: any[]) => {
      if (cancelled || ordered.length === 0) return;
      setBots(ordered);
      setBotsLoading(false);
      // A `?bot=<id>` deep link means "start talking to this agent", so it
      // opens the conversation rather than landing on the picker with that
      // card pre-highlighted — there is no highlighted state any more.
      // applyOrder runs up to 3× (cache → network → favorites reorder); the
      // phase check keeps later passes from remounting a live chat.
      if (botIdParam && phaseRef.current === 'pick') {
        const target = ordered.find(b => b.chatbot_id === botIdParam);
        if (target) {
          setSelectedBotId(target.chatbot_id);
          setPhase('chatting');
        } else {
          // Deleted, archived or out-of-scope bot id. `selectedBotId` was
          // seeded from the URL at mount, so leaving it set would keep the
          // agent-stroke SVG mounted over the picker with no agent chosen.
          setSelectedBotId(null);
        }
      }
    };

    // Phase 0: hydrate from cache before any network call goes out.
    // Per-tab (sessionStorage) so a different user logging into the
    // same tab can't read this user's bot list before the wipe.
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Array.isArray(cached) && cached.length > 0) {
          applyOrder(cached);
        }
      }
    } catch { /* corrupt cache — ignore, fall back to network */ }

    // Phase B: kick off favorites + recents in the background.
    const personalizationPromise = Promise.allSettled([
      botService.getFavoriteBots(),
      botService.getRecentBots(5),
    ]).then(([favRes, recentRes]) => ({
      favorites: favRes.status === 'fulfilled' ? favRes.value : [],
      recents:   recentRes.status === 'fulfilled' ? recentRes.value : [],
    }));

    // Phase A: refresh from server (replaces the cached render).
    // Uses the lightweight `/chatbots/carousel` endpoint — minimal
    // payload + Redis cached server-side per (tenant, user), so the
    // first visitor warms the cache for everyone else.
    const renderFromAll = async (): Promise<any[]> => {
      try {
        const all = await botService.getCarouselBots();
        applyOrder(all);
        if (all.length > 0) {
          try { sessionStorage.setItem(cacheKey, JSON.stringify(all)); }
          catch { /* quota or sandbox — non-fatal */ }
        }
        return all;
      } catch (e) {
        console.error('[MainPage] getCarouselBots failed:', e);
        return [];
      }
    };

    (async () => {
      let all = await renderFromAll();
      if (cancelled) return;

      // Cold-start race retry: if the very first fetch came back
      // empty, retry once after 1.2s. (JWT interceptor race window.)
      if (all.length === 0) {
        console.warn('[MainPage] first getBots returned empty; retrying in 1200ms');
        await new Promise(r => setTimeout(r, 1200));
        if (cancelled) return;
        all = await renderFromAll();
      }
      if (cancelled) return;

      // Phase B re-order: bring favorites + recents to the front.
      // NOTE: an empty `all` (network hiccup / cold-start race) must NOT skip
      // this block. The roster may already be painted from the sessionStorage
      // cache, and bailing here left every star hollow and the category tabs
      // suppressed even though the user's favourites had resolved fine. The
      // re-order below no-ops safely when `all` is empty.
      const { favorites, recents } = await personalizationPromise;
      if (cancelled) return;
      // Record WHICH bots are favorites vs recents before the two lists get
      // flattened into a single order below — the category tabs and the
      // filled-in stars both read this.
      setFavoriteIds(new Set(favorites.map((b: any) => b.chatbot_id)));
      setRecentIds(new Set(recents.map((b: any) => b.chatbot_id)));
      if (favorites.length === 0 && recents.length === 0) return;
      const seen = new Set<string>();
      const ordered: any[] = [];
      const push = (bot: any) => {
        if (!bot || seen.has(bot.chatbot_id)) return;
        seen.add(bot.chatbot_id);
        ordered.push(bot);
      };
      favorites.forEach(push);
      recents.forEach(push);
      all.forEach(push);
      applyOrder(ordered);
      try { sessionStorage.setItem(cacheKey, JSON.stringify(ordered)); }
      catch { /* non-fatal */ }
    })();

    return () => { cancelled = true; };
    // conciergeEnabled is read inside (conciergeOffset) but deliberately NOT
    // a dep — a flag flip must not refire the bot fetches. Re-centering on a
    // flip is handled by the effect below.
  }, [user?.user_id, user?.email]);



  const getGreeting = (): string => {
    // Title-case the displayed username — records are often stored lowercase
    // (or pulled from the email's local-part), which reads as sloppy in the
    // "Shalom <name>" greeting. capitalizeName handles multi-word names too.
    // The auth/login + verify responses carry the display name as
    // `user_full_name` (the DB column) — NOT `full_name` — so prefer it; keep
    // `full_name` as a fallback for any code path that hydrates the user from
    // the admin `to_dict()` shape, then the email local-part as a last resort.
    const rawName = user?.user_full_name || user?.full_name || user?.email?.split('@')[0] || '';
    const name = capitalizeName(rawName);
    // Per-tenant custom greeting (brand.terminology.greeting) wins over the
    // i18n default. Supports a `{{name}}` placeholder so tenants keep the
    // personalization. Falls back to i18n when unset.
    const custom = brand.terminology?.greeting;
    // Replacer function (not a string) so `$`-sequences in a user's name
    // (e.g. "$1", "$&") are inserted literally, not treated as replacement refs.
    if (custom) return custom.replace(/\{\{\s*name\s*\}\}/g, () => name);
    return t('dashboard.welcome', { name });
  };


  const selectedBot = bots.find((b: any) => b.chatbot_id === selectedBotId);

  // The carousel renders this derived array, with the synthetic Concierge
  // card prepended (index 0) so it's the pre-selected CENTER item. Real bots
  // keep all their own behavior; the synthetic id is special-cased in the
  // sync/select handlers below so it never reaches chatbot loading. With the
  // flag off `carouselBots === bots`.
  const carouselBots: any[] = useMemo(
    () =>
      conciergeEnabled
        ? [
            {
              ...buildConciergeCarouselBot(t('concierge.title')),
              chatbot_description: t('concierge.greetingSub'),
            },
            ...bots,
          ]
        : bots,
    [bots, conciergeEnabled, t],
  );

  // Favourite toggle from a card's star. Optimistic: the star flips at once
  // and only reverts if the request fails, because waiting on a round-trip
  // for a two-state icon feels broken.
  //
  // KNOWN BACKEND LIMITATION — favouriting a cross-tenant agent 404s.
  //   This picker lists agents via `apply_chat_access_filter` (the shared
  //   visibility service), which includes agents owned by OTHER tenants that
  //   the viewer can reach — descendants for a group_admin, everything for a
  //   super_admin. But `POST /chatbots/<id>/favorite` checks existence with a
  //   raw `Chatbot.query.filter_by(tenant_id=g.tenant_id, …)`, so any agent
  //   outside the viewer's own tenant is rejected with 404. Verified live:
  //   6 of 11 agents favourited, the 5 owned by eng-dept / cohen-law failed.
  //
  //   The star therefore flips and reverts for those agents. That is the
  //   revert path below doing its job, NOT a bug in this component.
  //
  //   Fix belongs server-side: route the existence check through the same
  //   `apply_chat_access_filter` the listing uses, in `favorite_bot` and
  //   `unfavorite_bot` (backend/modules/chatbots/api/routes.py). Safe — the
  //   favourites row stores the VIEWER's tenant_id, which is what the table's
  //   RLS policy keys on, and the GET already joins chatbots without a tenant
  //   filter so cross-tenant favourites read back correctly.
  //   Owner: Segev. Flagged in the PR rather than patched here.
  const handleToggleFavorite = useCallback(async (bot: any) => {
    const id = bot.chatbot_id;
    const wasFavorite = favoriteIds.has(id);
    const flip = (add: boolean) => setFavoriteIds(prev => {
      const next = new Set(prev);
      if (add) next.add(id); else next.delete(id);
      return next;
    });

    flip(!wasFavorite);
    try {
      await botService.toggleFavorite(id, wasFavorite);
    } catch (err) {
      console.error('[MainPage] toggleFavorite failed', err);
      flip(wasFavorite);
    }
  }, [favoriteIds]);

  const handleNavigate = useCallback((path: string) => {
    window.history.replaceState(null, '', `/#${path}`);
  }, []);

  // Opening an agent — the ONLY action on this page. A card is a door:
  // one click mounts the conversation, with no intermediate selected state
  // and no composer to fill in first. The synthetic Concierge card routes to
  // /concierge instead, which is its own full surface.
  //
  // chatKey is bumped so ConversationPanel remounts cleanly when the user
  // returns via "new chat" and picks a different agent.
  const handleOpenAgent = useCallback((bot: any) => {
    if (isConciergeCarouselBot(bot)) {
      navigate(tenantPath('/concierge'));
      return;
    }
    // Clear the hover hue explicitly: opening a chat unmounts the gallery
    // without ever firing its onMouseLeave, so a stale colour would otherwise
    // keep tinting the hero arc after a later "new chat" reset.
    setHoveredAgentColor(null);
    setSelectedBotId(bot.chatbot_id);
    setChatKey(prev => prev + 1);
    setPhase('chatting');
    // Journey detection: picking IS starting — the tour's first Try.
    emitJourneyEvent(JOURNEY_EVENTS.conversationStarted, { chatbotId: bot.chatbot_id });
  }, [navigate, tenantPath]);

  // Branch from a message on the home-page chat: hand off to ChatPage with an
  // autoBranch param so its existing branch handler creates the branch and
  // mounts the rail. Without this, MainPage's panel can't render branches.
  //
  // Guard: only act when MainPage is the visible route. Without this,
  // a brief overlap with ChatPage's mounted listener (route transitions,
  // StrictMode double-mount) makes a single fork click create TWO branches —
  // ChatPage's handler creates one directly, and MainPage's handler also
  // navigates to ?autoBranch=<msg> which fires branchRequested AGAIN inside
  // ChatPage and creates a second one.
  useEffect(() => {
    const handler = (event: CustomEvent<{ conversationId: string; messageId: string }>) => {
      // location.pathname here is the tenant-scoped home, e.g. `/team-alpha`
      // (no trailing slash from React Router 6) or `/team-alpha/`. We
      // detect "are we on the home of any tenant?" by checking that the
      // path has exactly one segment (the tenant slug).
      const segments = location.pathname.split('/').filter(Boolean);
      if (segments.length !== 1) return;

      const { conversationId, messageId } = event.detail;
      navigate(tenantPath(`/chat?conversation=${conversationId}&autoBranch=${messageId}`));
    };
    window.addEventListener('branchRequested', handler as unknown as EventListener);
    return () => window.removeEventListener('branchRequested', handler as unknown as EventListener);
  }, [navigate, tenantPath, location.pathname]);

  // Redirect if we already have a conversation param
  if (conversationParam) {
    navigate(tenantPath(`/chat?conversation=${conversationParam}`), { replace: true });
    return null;
  }

  const isPreChat = phase === 'pick';


  // Agent color → hue map, sourced from the tenant's agent palette so the
  // selection stroke follows the brand. Palette positions follow the canonical
  // order (blue, green, red, yellow); each falls back to the M² coin hex when
  // the brand palette is shorter. If the bot has no explicit avatar_color, we
  // derive one from its name the same way BotAvatar does, so the avatar
  // texture, stripe, and send button all agree on a single color.
  const PALETTE_ORDER = ['blue', 'green', 'red', 'yellow'] as const;
  const agentHueMap: Record<string, string> = Object.fromEntries(
    PALETTE_ORDER.map((name, i) => [name, brand.agentColors?.[i] ?? SOLID_AGENT_COLORS[name]]),
  );
  const resolvedAgentColor = selectedBot
    ? resolveAgentTextureColor(selectedBot.avatar_color, selectedBot.chatbot_name || '')
    : 'blue';
  const selectedAgentHue = agentHueMap[resolvedAgentColor] || brand.agentColors?.[0] || SOLID_AGENT_COLORS.blue;

  return (
    <div className={`home-layout phase-${phase} entry-${entryPhase}`}>
      {entryPhase !== 'done' && (
        <div className={`home-splash home-splash-${entryPhase}`} aria-hidden="true">
          <img src={splashIcon} alt="" className="home-splash-logo" />
        </div>
      )}
      <Sidebar />

      <main
        className="home-main"
        /* Drives --semi-color on the hero arc. Hover wins so the shape
           answers the pointer; otherwise it follows the open chat's agent. */
        data-agent-color={hoveredAgentColor || resolvedAgentColor}
        data-home-semi={semicircleEnabled ? 'true' : undefined}
      >
        {/* White-labeled hanging semicircle — decorative shape that "drops"
            from the top of the card and sits behind the greeting. Hidden
            during chat so it doesn't crowd the conversation header. Only
            rendered when the active brand opts in (M² preset on by default;
            base brand off). Color follows the selected agent unless the
            brand sets a fixed `homeSemicircle.color` override. */}
        {semicircleEnabled && isPreChat && (
          /* Plain decorative band — flat full-width rectangle, purely a
             background color fill. Deliberately NOT tied to the greeting's
             height/wrap state: it just paints the top ~third of the card and
             never constrains where text sits. */
          <div
            className="welcome-semi"
            aria-hidden="true"
            style={semicircleColorOverride ? { ['--semi-color' as string]: semicircleColorOverride } : undefined}
          />
        )}

        {/* Agent-color stroke — traces the card's edge in the agent's colour
            when an agent is selected, then fades away. Key re-mounts the SVG
            so it replays on each pick. Corner radius comes from CSS (it
            tracks the card's own radius per breakpoint), not from here. */}
        {selectedBotId && (
          <svg
            key={`agent-stroke-${selectedBotId}-${chatKey}`}
            className="agent-stroke-frame"
            aria-hidden="true"
          >
            <rect
              className="agent-stroke-path"
              x="1"
              y="1"
              width="calc(100% - 2px)"
              height="calc(100% - 2px)"
              pathLength="100"
              fill="none"
              stroke={selectedAgentHue}
            />
          </svg>
        )}
        {/* ───── Welcome elements — animate out when chatting ───── */}

        {/* Greeting — two lines: "Shalom, name." then "How can I help you today?" */}
        <div className={`welcome-greeting ${phase === 'chatting' ? 'greeting-exit' : ''}`}>
          <h1 className="welcome-title">
            <span dir="auto" className="welcome-title-line">{getGreeting()}</span>
            <span className="welcome-title-line welcome-title-question" dir="auto">
              {brand.terminology?.greetingQuestion || t('dashboard.welcomeQuestion')}
            </span>
          </h1>
        </div>

        {/* ─── Middle zone (flex:1 on mobile) ─────────────────────────────
            Wrapping grid + carousel + intro inside one container so the
            mobile layout can give it `flex: 1` and vertically center the
            agents between the top header strip and the bottom input.
            On desktop the wrapper is just a passive column container —
            grid keeps its existing margins. */}
        {isPreChat && (
        <div className="agents-section">
        {/* Single-agent case: no picker, but still surface the auto-
            selected agent's avatar + name so the user knows who they're
            chatting with. Reuses the carousel-item styling for visual
            consistency with the multi-agent layout. */}
        {!botsLoading && carouselBots.length === 1 && (
          <div className="agent-carousel single-agent">
            <div className="carousel-row">
              <div className="carousel-viewport">
                <div className="carousel-track" style={{ transform: 'translateX(0)' }}>
                  <div
                    className={`carousel-item current ${isConciergeCarouselBot(carouselBots[0]) ? CONCIERGE_CARD_CLASS : ''}`}
                    data-agent-color={resolveAgentTextureColor(carouselBots[0].avatar_color, carouselBots[0].chatbot_name || '')}
                    style={{ transform: 'scale(1)', opacity: 1 }}
                    onClick={() => handleOpenAgent(carouselBots[0])}
                  >
                    <div className="carousel-item-card">
                      {isConciergeCarouselBot(carouselBots[0]) && (
                        <span className="concierge-card-badge">
                          {t('concierge.card.badge')}
                        </span>
                      )}
                      <div className="agent-tile-avatar">
                        <BotAvatar
                          name={carouselBots[0].chatbot_name || 'AI'}
                          size={60}
                          colorOverride={carouselBots[0].avatar_color}
                          variantOverride={carouselBots[0].avatar_variant ?? undefined}
                        />
                        {isConciergeCarouselBot(carouselBots[0]) && (
                          <span className="concierge-card-cta">
                            {t('concierge.card.cta')}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="carousel-item-name">{carouselBots[0].chatbot_name}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Agent gallery — replaces the horizontal carousel. Every card is a
            door: one click opens the conversation. The Concierge is the first
            card and routes to /concierge through the same callback.

            The single-agent layout above is deliberately separate: a grid of
            one is pointless, but it obeys the same one-click rule. */}
        {!botsLoading && carouselBots.length > 1 && (
          <AgentGallery
            bots={carouselBots}
            onOpenAgent={handleOpenAgent}
            onHoverAgent={setHoveredAgentColor}
            onToggleFavorite={handleToggleFavorite}
            favoriteIds={favoriteIds}
            recentIds={recentIds}
          />
        )}

        </div>
        )}

        {/* ───── Chat layer — mounts the moment a card is clicked ─────
            No autoSend: the user hasn't typed anything yet. The panel opens
            an empty conversation with the chosen agent and its own composer
            takes it from there. */}
        {phase === 'chatting' && selectedBotId && (
          <div className="chat-layer">
            <ConversationPanel
              key={`home-chat-${chatKey}`}
              panelId="home"
              initialBotId={selectedBotId}
              /* Pre-known agent color hint — eliminates the blue-flash
                 between mount and bot-load. Since MainPage already knows
                 which agent the user picked (it's in `selectedBot`), the
                 chat surface should open ALREADY tinted with that
                 agent's color, not blue-then-jump-to-yellow. */
              initialAgentColor={resolvedAgentColor}
              onNavigate={handleNavigate}
              onBranchCreated={() => {/* delegated to ChatPage via branchRequested → autoBranch param */}}
            />
          </div>
        )}

      </main>
    </div>
  );
}
